import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Icon } from "@/lib/components/ui/Icon/Icon";
import { useAssistantsQuery } from "@/lib/api/assistant/useAssistantsQuery";
import { useChatsContext } from "@/lib/context/ChatsProvider/hooks/useChatsContext";
import { useOrganisationContext } from "@/lib/context/OrganisationProvider";
import { THREADS_REFRESH_INTERVAL_MS } from "@/lib/constants/threads";
import { useAuth } from "@/lib/hooks";

import styles from "./Threads.module.scss";
import { ThreadItem } from "./ThreadsSection/ThreadItem/ThreadItem";
import { useFetchAgentThreads } from "./hooks/useFetchAgentThreads";
import { getThreadOwnerName } from "./utils";

type Tab = "all" | "favorites";

const SEARCH_DEBOUNCE_MS = 400;

const isThreadUnread = (
  lastMessageAt: string | null,
  lastReadAt: string | null,
  creationTime: string
): boolean => {
  const timestamp = lastMessageAt ?? creationTime;
  if (!timestamp) return false;
  if (!lastReadAt) return true;
  return new Date(timestamp).getTime() > new Date(lastReadAt).getTime();
};

export const Threads = (): JSX.Element => {
  const [activeTab, setActiveTab] = useState<Tab>("all");
  const [searchValue, setSearchValue] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const { allChats, hasMore, isLoading, threadsRefreshToken } =
    useChatsContext();
  const { fetchThreads, fetchMoreThreads } = useFetchAgentThreads();
  const { t } = useTranslation("chat");
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { isAdmin } = useOrganisationContext();
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const sentinelRef = useRef<HTMLDivElement>(null);
  const { data: assistants = [] } = useAssistantsQuery();

  const ownerNames = useMemo(() => {
    return assistants.reduce((acc, owner) => {
      acc[owner.id] = owner.name;
      return acc;
    }, {} as Record<string, string>);
  }, [assistants]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value.trim());
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const isFavoriteFilter = activeTab === "favorites" ? true : undefined;

  useEffect(() => {
    if (!isAuthenticated || isAuthLoading) return;

    void fetchThreads(debouncedSearch || undefined, isFavoriteFilter, true);
  }, [
    debouncedSearch,
    fetchThreads,
    isAuthenticated,
    isAuthLoading,
    isFavoriteFilter,
  ]);

  useEffect(() => {
    if (!threadsRefreshToken || !isAuthenticated || isAuthLoading) return;

    void fetchThreads(debouncedSearch || undefined, isFavoriteFilter);
    // Only refetch on explicit refresh requests; filter changes are handled above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadsRefreshToken]);

  const visibleThreads = useMemo(() => {
    const threads = allChats.filter(
      (thread) =>
        !!thread.assistant_id && (isAdmin || thread.assistant_id in ownerNames)
    );

    return [...threads].sort((a, b) => {
      const aTime = a.last_message_at ?? a.creation_time;
      const bTime = b.last_message_at ?? b.creation_time;
      return new Date(bTime).getTime() - new Date(aTime).getTime();
    });
  }, [allChats, isAdmin, ownerNames]);

  useEffect(() => {
    if (!isAuthenticated || isAuthLoading) {
      return;
    }
    const interval = setInterval(
      () => void fetchThreads(debouncedSearch || undefined, isFavoriteFilter),
      THREADS_REFRESH_INTERVAL_MS
    );

    return () => clearInterval(interval);
  }, [
    fetchThreads,
    isAuthenticated,
    isAuthLoading,
    debouncedSearch,
    isFavoriteFilter,
  ]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          void fetchMoreThreads(debouncedSearch || undefined, isFavoriteFilter);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoading, debouncedSearch, isFavoriteFilter, fetchMoreThreads]);

  return (
    <div className={styles.threads_container}>
      <div className={styles.content}>
        <div className={styles.tabs}>
          <button
            type="button"
            className={`${styles.tab} ${
              activeTab === "all" ? styles.active : ""
            }`}
            onClick={() => setActiveTab("all")}
          >
            {t("allThreads", "All")}
          </button>
          <button
            type="button"
            className={`${styles.tab} ${
              activeTab === "favorites" ? styles.active : ""
            }`}
            onClick={() => setActiveTab("favorites")}
          >
            {t("favorites", "Favorites")}
          </button>
        </div>
        <div className={styles.threads_list}>
          {visibleThreads.map((chat) => (
            <ThreadItem
              key={chat.chat_id}
              chatHistoryItem={chat}
              agentName={getThreadOwnerName(chat, ownerNames)}
              isUnread={isThreadUnread(
                chat.last_message_at,
                chat.last_read_at,
                chat.creation_time
              )}
            />
          ))}
          {visibleThreads.length === 0 && (
            <div className={styles.no_threads}>
              {t("noThreads", "No threads found")}
            </div>
          )}
          {hasMore && <div ref={sentinelRef} className={styles.sentinel} />}
        </div>
        <div className={styles.search_container}>
          <input
            type="text"
            className={styles.search_input}
            value={searchValue}
            onChange={(event) => handleSearchChange(event.target.value)}
            placeholder={t("searchThread", "Search Thread")}
            aria-label={t("searchThread", "Search Thread")}
          />
          <span className={styles.search_icon}>
            <Icon name="search" size="small" color="white" />
          </span>
        </div>
      </div>
    </div>
  );
};
